<template>
  <!-- Incoming Call Alert -->
  <div v-if="incomingCall" class="incoming-call-overlay">
    <div class="call-alert-container">
      <!-- Background Pulse Effect -->
      <div class="pulse-background"></div>

      <!-- Main Alert Card -->
      <div class="call-alert-card">
        <!-- Header with Emergency Badge -->
        <div class="alert-header">
          <div class="emergency-badge">
            <div class="emergency-pulse"></div>
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>EMERGENCY CALL</span>
          </div>
          <div
            class="priority-indicator"
            :class="getPriorityClass(incomingCall.emergencyType)"
          >
            {{ getPriorityText(incomingCall.emergencyType) }}
          </div>
        </div>

        <!-- Caller Information -->
        <div class="caller-section">
          <div class="caller-avatar">
            <div class="avatar-pulse"></div>
            <i class="bi bi-person-fill"></i>
          </div>
          <div class="caller-details">
            <h2 class="caller-name">{{ incomingCall.callerName }}</h2>
            <div class="caller-meta">
              <div class="meta-item">
                <i class="bi bi-geo-alt-fill"></i>
                <span>{{ incomingCall.location }}</span>
              </div>
              <div class="meta-item">
                <i class="bi bi-clock-fill"></i>
                <span>Just now</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Emergency Details -->
        <div class="emergency-section">
          <div class="emergency-type">
            <i class="bi" :class="getEmergencyIcon(incomingCall.emergencyType)"></i>
            <span>{{ formatEmergencyType(incomingCall.emergencyType) }}</span>
          </div>
          <div class="emergency-description mb-1">Immediate response required</div>
          <div v-if="incomingCall.score > 0" class="emergency-type mb-1">
            <i class="bi-exclamation-circle-fill text-warning"></i>
            <span>Fraud probability: {{ incomingCall.score }}%</span>
          </div>
          <div v-if="incomingCall.warnings" class="emergency-description">
            {{ incomingCall.warnings }}
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="action-section">
          <button @click="acceptCall" class="btn accept-btn">
            <div class="btn-content">
              <div class="btn-icon">
                <i class="bi bi-telephone-fill"></i>
              </div>
              <div class="btn-text">
                <span class="btn-title">Accept Call</span>
                <span class="btn-subtitle">Connect immediately</span>
              </div>
            </div>
            <div class="btn-glow"></div>
          </button>

          <button @click="rejectCall(incomingCall.callId)" class="btn reject-btn">
            <div class="btn-content">
              <div class="btn-icon">
                <i class="bi bi-telephone-x-fill"></i>
              </div>
              <div class="btn-text">
                <span class="btn-title">Decline</span>
                <span class="btn-subtitle">Reject call</span>
              </div>
            </div>
          </button>
        </div>

        <!-- Visual Indicator -->
        <div class="visual-indicator">
          <div class="signal-bars">
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
            <div class="bar"></div>
          </div>
          <span class="indicator-text">LIVE INCOMING CALL</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import ringingSound from "@/assets/ringtones/incoming.wav";
import emergencyTypeHelper from "@/utils/emergencyTypeHelper";

const incomingCall = ref(null);

// eslint-disable-next-line
const emit = defineEmits(["call-accepted", "call-rejected"]);

let ringingAudio = null;

const stopNotificationSound = () => {
  if (ringingAudio) {
    ringingAudio.pause();
    ringingAudio.currentTime = 0;
    ringingAudio = null;
  }
};

const formatEmergencyType = (type) => {
  return emergencyTypeHelper.formatEmergencyType(type);
};

const playNotificationSound = () => {
  stopNotificationSound();
  ringingAudio = new Audio(ringingSound);
  ringingAudio.loop = true;
  ringingAudio.play().catch((e) => console.log("Could not play notification sound:", e));
};

const acceptCall = () => {
  stopNotificationSound();
  window.dispatchEvent(
    new CustomEvent("callAccepted", {
      detail: incomingCall.value,
    })
  );
  incomingCall.value = null;
};

const rejectCall = (callId) => {
  window.dispatchEvent(new CustomEvent("callRejected", { detail: callId }));
  dispatchRefreshNeeded();
  stopNotificationSound();
  incomingCall.value = null;
};

// Utility functions for dynamic styling
const getPriorityClass = (emergencyType) => {
  const priorityMap = {
    Medical: "critical",
    Fire: "critical",
    Police: "high",
    Rescue: "high",
    General: "medium",
  };
  return priorityMap[emergencyType] || "medium";
};

const getPriorityText = (emergencyType) => {
  const textMap = {
    Medical: "CRITICAL",
    Fire: "CRITICAL",
    Police: "HIGH PRIORITY",
    Rescue: "HIGH PRIORITY",
  };
  return textMap[emergencyType] || "PRIORITY";
};

const getEmergencyIcon = (emergencyType) => {
  const iconMap = {
    Medical: "bi-heart-pulse-fill",
    Fire: "bi-fire",
    Police: "bi-shield-fill",
    Rescue: "bi-life-preserver",
  };
  return iconMap[emergencyType] || "bi-exclamation-circle-fill";
};

// SignalR Event Handlers
const handleIncomingEmergencyCall = (event) => {
  incomingCall.value = event.detail;
  playNotificationSound();
};

const handleCallEnded = (event) => {
  if (incomingCall.value?.callId == event.detail) {
    dispatchRefreshNeeded();
    stopNotificationSound();
    incomingCall.value = null;
  }
};

const dispatchRefreshNeeded = () => {
  window.dispatchEvent(
    new CustomEvent("recentCallsRefreshNeeded", { detail: "refresh_recent_calls" })
  );
};

const initializeListeners = () => {
  window.addEventListener("incomingEmergencyCall", handleIncomingEmergencyCall);
  window.addEventListener("callEnded", handleCallEnded);
};

const detachListeners = () => {
  window.removeEventListener("incomingEmergencyCall", handleIncomingEmergencyCall);
  window.removeEventListener("callEnded", handleCallEnded);
};

onMounted(() => {
  initializeListeners();
});

onUnmounted(() => {
  detachListeners();
});
</script>

<style scoped>
.incoming-call-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  animation: fadeIn 0.3s ease-out;
}

.pulse-background {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(220, 53, 69, 0.15) 0%, transparent 70%);
  animation: pulseGlow 2s ease-in-out infinite;
}

.call-alert-container {
  position: relative;
  z-index: 2;
  animation: slideInUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.call-alert-card {
  background: linear-gradient(135deg, #1a1a1a 0%, #2d1f1f 100%);
  border: 1px solid rgba(220, 53, 69, 0.3);
  border-radius: 20px;
  padding: 2rem;
  width: 420px;
  box-shadow: 0 20px 60px rgba(220, 53, 69, 0.3), 0 0 0 1px rgba(220, 53, 69, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}

.call-alert-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #dc3545, transparent);
  animation: shimmer 3s ease-in-out infinite;
}

.alert-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.emergency-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(220, 53, 69, 0.15);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  border: 1px solid rgba(220, 53, 69, 0.3);
  position: relative;
}

.emergency-pulse {
  position: absolute;
  top: 50%;
  left: 0.5rem;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: #dc3545;
  border-radius: 50%;
  animation: emergencyPulse 1.5s ease-in-out infinite;
}

.emergency-badge i {
  color: #dc3545;
  font-size: 1rem;
}

.emergency-badge span {
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.5px;
}

.priority-indicator {
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.priority-indicator.critical {
  background: rgba(220, 53, 69, 0.3);
  color: #ff6b7a;
  border: 1px solid rgba(220, 53, 69, 0.5);
}

.priority-indicator.high {
  background: rgba(255, 193, 7, 0.2);
  color: #ffd351;
  border: 1px solid rgba(255, 193, 7, 0.4);
}

.priority-indicator.medium {
  background: rgba(13, 110, 253, 0.2);
  color: #6ea8fe;
  border: 1px solid rgba(13, 110, 253, 0.4);
}

.caller-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.caller-avatar {
  position: relative;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #dc3545, #ff6b7a);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.avatar-pulse {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border: 2px solid #dc3545;
  border-radius: 50%;
  animation: avatarPulse 2s ease-in-out infinite;
}

.caller-details {
  flex: 1;
}

.caller-name {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, #fff, #ff6b7a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.caller-meta {
  display: flex;
  gap: 1rem;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: #aaa;
  font-size: 0.8rem;
}

.meta-item i {
  color: #dc3545;
  font-size: 0.7rem;
}

.emergency-section {
  background: rgba(220, 53, 69, 0.1);
  padding: 1rem;
  border-radius: 12px;
  border: 1px solid rgba(220, 53, 69, 0.2);
  margin-bottom: 1.5rem;
}

.emergency-type {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ff6b7a;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.3rem;
}

.emergency-type i {
  font-size: 1.1rem;
}

.emergency-description {
  color: #aaa;
  font-size: 0.8rem;
}

.action-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.btn {
  flex: 1;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-content {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  position: relative;
  z-index: 2;
}

.btn-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.btn-text {
  text-align: left;
  flex: 1;
}

.btn-title {
  display: block;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 0.2rem;
}

.btn-subtitle {
  display: block;
  font-size: 0.7rem;
  opacity: 0.8;
}

.accept-btn {
  background: linear-gradient(135deg, #dc3545, #ff6b7a);
  color: white;
}

.accept-btn .btn-icon {
  background: rgba(255, 255, 255, 0.2);
}

.accept-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(220, 53, 69, 0.4);
}

.btn-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.accept-btn:hover .btn-glow {
  left: 100%;
}

.reject-btn {
  color: #aaa;
}

.reject-btn .btn-icon {
  background: rgba(255, 255, 255, 0.05);
  color: #aaa;
}

.reject-btn:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  transform: translateY(-1px);
}

.visual-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(220, 53, 69, 0.05);
  border-radius: 10px;
  border: 1px solid rgba(220, 53, 69, 0.1);
}

.signal-bars {
  display: flex;
  align-items: end;
  gap: 3px;
  height: 20px;
}

.signal-bars .bar {
  width: 3px;
  background: #dc3545;
  border-radius: 2px;
  animation: signalWave 1.5s ease-in-out infinite;
}

.signal-bars .bar:nth-child(1) {
  height: 5px;
  animation-delay: 0s;
}
.signal-bars .bar:nth-child(2) {
  height: 8px;
  animation-delay: 0.1s;
}
.signal-bars .bar:nth-child(3) {
  height: 12px;
  animation-delay: 0.2s;
}
.signal-bars .bar:nth-child(4) {
  height: 16px;
  animation-delay: 0.3s;
}
.signal-bars .bar:nth-child(5) {
  height: 20px;
  animation-delay: 0.4s;
}

.indicator-text {
  color: #dc3545;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes pulseGlow {
  0%,
  100% {
    opacity: 0.5;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 0.8;
    transform: translate(-50%, -50%) scale(1.05);
  }
}

@keyframes emergencyPulse {
  0%,
  100% {
    opacity: 1;
    transform: translateY(-50%) scale(1);
  }
  50% {
    opacity: 0.5;
    transform: translateY(-50%) scale(1.2);
  }
}

@keyframes avatarPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0;
    transform: scale(1.3);
  }
}

@keyframes signalWave {
  0%,
  100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(0.3);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* Responsive Design */
@media (max-width: 480px) {
  .call-alert-card {
    width: 90vw;
    margin: 1rem;
    padding: 1.5rem;
  }

  .caller-section {
    flex-direction: column;
    text-align: center;
  }

  .action-section {
    flex-direction: column;
  }

  .caller-meta {
    justify-content: center;
  }
}
</style>
